import { Injectable } from '@angular/core';
import { Review } from './models/review.model';
import { Observable } from 'rxjs';
import {
  ACCEPT_VERSION_HEADER,
  AuthHttpService,
} from '../auth-data/auth-http.service';
import { LoggerService } from '../shared/services/logger/logger.service';
import { map } from 'rxjs/operators';
import { IResponseInfo } from '../http/http.data';
import { List } from 'immutable';
import { ReviewRequest } from './models/review-request.model';
import { ReviewClient } from './models/review-client.model';
import {
  IReviewSettings,
  IReviewSettingsRequestPayload,
  ISendRequestReviewRequestPayload,
} from './review-data.data';
import { ReviewSummary } from './models/review-summary.model';
import { serialize } from 'i2g-ng-utils';

export const ACCEPT_VERSION = '1.1.0';
export const REVIEW_SETTINGS_ACCEPT_VERSION = '1.0.0';

@Injectable()
export class ReviewDataService {
  baseUrl = '/business';
  reviewSettingsPath = '/reviews/settings';

  protected headers: any = {
    [ACCEPT_VERSION_HEADER]: ACCEPT_VERSION,
  };

  protected reviewSettingsHeaders: any = {
    [ACCEPT_VERSION_HEADER]: REVIEW_SETTINGS_ACCEPT_VERSION,
  };

  constructor(
    private _loggerService: LoggerService,
    private _authHttpService: AuthHttpService
  ) {}

  fetchReviews(): Observable<List<Review>> {
    this._loggerService.log('fetch reviews from network');

    const url = `${this.baseUrl}/reviews`;
    const params = { page_size: 100 }; // for MVP, set fixed page size to 100

    return this._authHttpService.get(url, params, this.headers).pipe(
      map(({ body, ..._ }: IResponseInfo) => {
        return List<Review>(
          (body['reviews'] || []).map((review) => new Review(review))
        );
      })
    );
  }

  fetchReviewSummary(): Observable<ReviewSummary> {
    this._loggerService.log('fetch review summary from network');

    const url = `${this.baseUrl}/reviews/summary`;

    return this._authHttpService.get(url, null, this.headers).pipe(
      map(({ body, ..._ }: IResponseInfo) => {
        return new ReviewSummary(body);
      })
    );
  }

  fetchReviewDetail(reviewId: string): Observable<Review> {
    this._loggerService.log('fetch review from network');

    const url = `${this.baseUrl}/reviews/${reviewId}`;

    return this._authHttpService.get(url, null, this.headers).pipe(
      map(({ body, ..._ }: IResponseInfo) => {
        return new Review(body.review);
      })
    );
  }

  requestReview(clientId: string) {
    this._loggerService.log('fetch reviews request from network');

    const url = `${this.baseUrl}/reviews/requests`;
    const requestBody = {
      client_id: clientId,
    };

    return this._authHttpService.post(url, requestBody, this.headers).pipe(
      map(({ body, ..._ }: IResponseInfo) => {
        return new ReviewRequest(body);
      })
    );
  }

  fetchEligibleClients(): Observable<List<ReviewClient>> {
    this._loggerService.log(
      'fetch eligible clients for review request from network'
    );

    const url = `${this.baseUrl}/reviews/requests/eligible_clients`;

    return this._authHttpService.get(url, null, this.headers).pipe(
      map(({ body, ..._ }: IResponseInfo) => {
        return List<ReviewClient>(
          (body['eligible_clients'] || []).map(
            (client) => new ReviewClient(client)
          )
        );
      })
    );
  }

  sendRequestReview(
    requestPayload: ISendRequestReviewRequestPayload
  ): Observable<IResponseInfo> {
    this._loggerService.log('send request review from network');

    const url = `${this.baseUrl}/reviews/requests/email`;

    return this._authHttpService.post(url, requestPayload, this.headers);
  }

  fetchReviewSettings(): Observable<IReviewSettings> {
    this._loggerService.log('fetch review settings from network');

    const url = `${this.baseUrl}${this.reviewSettingsPath}`;

    return this._authHttpService
      .get(url, null, this.reviewSettingsHeaders)
      .pipe(
        map(
          ({ body }: IResponseInfo) =>
            (body as IReviewSettingsRequestPayload).review_settings
        )
      );
  }

  saveReviewSettingsNetwork(
    settings: IReviewSettingsRequestPayload
  ): Observable<IReviewSettings> {
    this._loggerService.log('update review settings: ', settings, 'to network');

    const url = `${this.baseUrl}${this.reviewSettingsPath}`;

    return this._authHttpService
      .put(url, serialize(settings), this.reviewSettingsHeaders)
      .pipe(
        map(
          ({ body }: IResponseInfo) =>
            (body as IReviewSettingsRequestPayload).review_settings
        )
      );
  }
}
